import {
	HeadContent,
	Scripts,
	type ErrorComponentProps,
	createRootRouteWithContext,
	retainSearchParams,
	stripSearchParams,
} from '@tanstack/react-router';
import * as v from 'valibot';
import appCssUrl from '../app.css?url';
import type { QueryClient } from '@tanstack/react-query';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import { FormDevtoolsPanel } from '@tanstack/react-form-devtools';
import type { PortalLocale } from '@worldcoo/monolith/portals';
import { I18nProvider } from '@lingui/react';
import { type I18n } from '@lingui/core';
import { loadLandingTranslation } from '@/lib/i18n';
import { bootstrapHeadScripts } from '@/lib/head-bootstrap';
import { lazy, Suspense, useEffect, useState } from 'react';
import { onToasterActivated, flushPendingToasts } from '@/lib/toast-signal';
import {
	getRuntimeAnalyticsCode,
	getRuntimeCdnUrl,
	getRuntimeImgproxyUrl,
	getRuntimeRecaptchaSiteKey,
	getRuntimeAdyenClientKey,
	getRuntimeUsercentricsSettingsId,
	getRuntimeUsercentricsDraft,
} from '@/lib/shared';
import { currentDocumentTrace } from '@/lib/ssr-trace';
import { loadGtag } from '@worldcoo/ui/lib/analytics';
import {
	clearCookiesForRefusedServices,
	loadUsercentrics,
	syncConsentCookie,
	syncUsercentricsBannerVisibility,
	syncUsercentricsLanguage,
	onUsercentricsConsentChanged,
	onUsercentricsReady,
} from '@worldcoo/ui/lib/usercentrics';
import { useBannerFreeRoute } from '@/lib/use-banner-free-route';
import type { UsercentricsDecision } from '@worldcoo/ui/lib/consent-cookie';
import { grantEmbeddedConsent } from '@/lib/consent-mode';
import { DefaultCatchBoundary } from './-components/default-catch-boundary';
import { NotFound } from './-components/not-found';

const Toaster = lazy(() => import('@worldcoo/ui/components/sonner').then((m) => ({ default: m.Toaster })));

export interface MyRouterContext {
	queryClient: QueryClient;
	// Request-scoped Lingui instance (see createLocaleI18n). The SSR render tree reads its
	// translations from this instance via <I18nProvider>, so concurrent requests in the same
	// worker can't clobber each other's active locale.
	i18n: I18n;
	baseUrl: string;
	portalId: string;
	locale: PortalLocale;
	// Whether the URL being rendered carries an explicit locale segment (`/es/...`) versus the
	// unprefixed root (`/...`). SEO components use it to keep the canonical URL pointing at the page
	// itself rather than at the prefixed hreflang variant (Lighthouse and Google flag a canonical that
	// matches a non-self hreflang alternate as invalid). It lives in the context, never in module
	// state, so concurrent SSR renders can't read each other's URL shape; the server dehydrates it and
	// the client re-applies it on hydrate, so the first client render agrees with the markup it is
	// hydrating (TEC-3607).
	hasLocalePrefix: boolean;
	isBot: boolean;
	isInIframe: boolean;
	// The CMP's decision as of this request, read from the mirror cookie the client keeps in sync. It lets the
	// SERVER render a gated embed the way the donor already chose, instead of rendering "not decided" and
	// having the client visibly correct it a moment later. Undefined means genuinely undecided — first visit,
	// or a cookie we could not read. See `syncConsentCookie`.
	consentDecision: UsercentricsDecision | undefined;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
	validateSearch: v.object({
		q: v.fallback(v.optional(v.string()), undefined),
		lang: v.fallback(
			v.optional(
				v.picklist([
					'ca',
					'ca-ES',
					'de',
					'de-DE',
					'en',
					'en-EN',
					'en-US',
					'en-GB',
					'es',
					'es-ES',
					'eu',
					'eu-ES',
					'fr',
					'fr-FR',
					'gl',
					'gl-ES',
					'it',
					'it-IT',
					'nl',
					'nl-NL',
					'pt',
					'pt-PT',
					'pt-BR',
				]),
			),
			undefined,
		),
		iframe: v.fallback(v.optional(v.literal(true)), undefined),
		city: v.fallback(v.optional(v.string()), undefined),
	}),
	search: {
		// TODO: retainSearchParams is not working correctly with default values and stripSearchParams,
		// so we're passing search={(prev) => prev} manually in the links for now
		middlewares: [stripSearchParams({ q: '' }), retainSearchParams(['lang', 'iframe', 'city'])],
	},
	// Reload translations when the locale from the URL (resolved by rewrite.input)
	// differs from the one loaded during router creation (which uses cookie/Accept-Language).
	// Reload the request-scoped instance (used by the SSR render tree) and only that one:
	// `loadLandingTranslation` activates the instance it is given, deliberately leaving the shared
	// module-global `i18n` alone on the server. See {@link createLocaleI18n}.
	beforeLoad: async ({ context }) => {
		if (context.i18n.locale !== context.locale) {
			await loadLandingTranslation(context.locale, context.i18n);
		}
	},
	head: () => {
		// Baking the trace ids into the HTML is what lets everything the page does afterwards — its API
		// calls, a crash it reports, the clicks leading up to that crash — attach to the same trace as the
		// render itself, instead of being four unrelated records.
		//
		// The list is fixed-length, unconditional and first-party, and `bootstrapHeadScripts` documents
		// why that is load-bearing: a conditional head entry is what produced the #418 `args[]=HTML`
		// reports on this route. Nothing may be appended here conditionally.
		const scripts = bootstrapHeadScripts(
			{
				cdnUrl: getRuntimeCdnUrl(),
				imgproxyUrl: getRuntimeImgproxyUrl(),
				analyticsCode: getRuntimeAnalyticsCode(),
				recaptchaSiteKey: getRuntimeRecaptchaSiteKey(),
				adyenClientKey: getRuntimeAdyenClientKey(),
				usercentricsSettingsId: getRuntimeUsercentricsSettingsId(),
				usercentricsDraft: getRuntimeUsercentricsDraft(),
			},
			currentDocumentTrace(),
		);
		return {
			meta: [
				{
					charSet: 'utf-8',
				},
				{
					name: 'viewport',
					content: 'width=device-width, initial-scale=1',
				},
			],
			scripts: scripts.map((children) => ({ children })),
		};
	},
	errorComponent: RootErrorComponent,
	notFoundComponent: RootNotFound,
	shellComponent: RootDocument,
});

// The error/not-found components read the request-scoped Lingui instance from route context
// rather than the shared global `i18n`. On the server the global can be flipped by a concurrent
// request mid-render, so rendering these through the global risks a locale mismatch between SSR
// and client hydration (React #418). See {@link createLocaleI18n}.
function RootErrorComponent(props: ErrorComponentProps) {
	const { i18n } = Route.useRouteContext();
	return (
		<I18nProvider i18n={i18n}>
			<DefaultCatchBoundary {...props} />
		</I18nProvider>
	);
}

function RootNotFound() {
	const { i18n } = Route.useRouteContext();
	return (
		<I18nProvider i18n={i18n}>
			<NotFound />
		</I18nProvider>
	);
}

function OnDemandToaster() {
	const [show, setShow] = useState(false);
	useEffect(() => onToasterActivated(() => setShow(true)), []);
	if (!show) return null;
	return (
		<Suspense>
			<ToasterWithFlush />
		</Suspense>
	);
}

function ToasterWithFlush() {
	useEffect(() => {
		flushPendingToasts();
	}, []);
	return (
		<Toaster
			className="whitespace-pre-line print:hidden"
			offset={{ top: 'calc(32px + env(safe-area-inset-top))' }}
			mobileOffset={{ top: 'calc(16px + env(safe-area-inset-top))' }}
			position="top-center"
			theme="light"
		/>
	);
}

function RootDocument({ children }: { children: React.ReactNode }) {
	const { locale, isInIframe } = Route.useRouteContext();
	const bannerFreeRoute = useBannerFreeRoute();
	const cdnUrl = import.meta.env.DEV ? '' : getRuntimeCdnUrl();
	const imgproxyUrl = getRuntimeImgproxyUrl();

	useEffect(() => {
		if (import.meta.env.DEV) {
			void import('react-grab');
		}
	}, []);

	// From an effect, so the tag is appended AFTER hydration has committed: adding a node to the head
	// before that would hand React a child the server never rendered, which is the very error this moved
	// out of `head()` to avoid. See `loadGtag`.
	useEffect(() => {
		loadGtag(getRuntimeAnalyticsCode());
	}, []);

	// The cookie consent banner, loaded from an effect for the same reason as the analytics tag above —
	// and more so, since injecting head scripts is what a CMP does. See `loadUsercentrics`.
	//
	// Skipped entirely when embedded: the integrator collected consent on the platform hosting the
	// iframe, so a second banner would ask a donor to consent twice for one journey. Not loading the
	// script at all — rather than loading it and hiding the banner — also keeps a third-party request out
	// of the merchant's page. `isInIframe` rather than the raw `?iframe=true` search param because the
	// server ORs that param with the `Sec-Fetch-*` headers, so it also covers a genuine cross-site embed
	// that arrived without the param.
	//
	// Everything that reads consent has to know this branch exists, or it ends up gating a third party on
	// a CMP that was never loaded and can never answer. The two that do: `grantEmbeddedConsent` below for
	// Consent Mode storage, and `useUsercentricsServiceConsent` for the per-service embeds.
	//
	// The page's locale is passed rather than left to the CMP to detect. It reads neither `<html lang>` nor
	// `navigator.language` at init, so without it the banner opens in the configuration's first language —
	// Spanish — on every non-Spanish URL. `syncUsercentricsLanguage` below cannot cover that: it runs on
	// mount while the CMP is still being fetched, where it is a no-op, and then never runs again because
	// the locale has not changed. That is exactly what shipped: `/en` came up with a Spanish banner, and
	// only switching language on the client put it right. See `loadUsercentrics`.
	//
	// Only the language the banner FIRST appears in: re-running this on a later switch changes nothing,
	// because `loadUsercentrics` injects the loader once per session and no-ops afterwards. Keeping the
	// banner in step from then on is `syncUsercentricsLanguage`'s business, below.
	//
	// `bannerFreeRoute` is likewise only read on the first run, and has to be passed here rather than acted
	// on afterwards: a legal page LOADED directly is the one case where the banner can be prevented instead
	// of closed once it is already on screen. Leaving that page re-opens it — see the effect below.
	useEffect(() => {
		if (isInIframe) return;
		loadUsercentrics(
			getRuntimeUsercentricsSettingsId(),
			getRuntimeUsercentricsDraft() === 'true',
			locale,
			bannerFreeRoute,
		);
	}, [isInIframe, locale, bannerFreeRoute]);

	// Keep the banner off the legal notice and the privacy policy, and bring it back on the way out. This is
	// the client-side-navigation half of the pair above: a donor who follows the footer link arrives with the
	// banner already open, so there it has to be closed rather than prevented. See
	// `syncUsercentricsBannerVisibility` for why closing is safe and why only the first layer is affected.
	//
	// Through `onUsercentricsReady` because this runs on mount, when the CMP has not been fetched yet and
	// there is nothing to close; without waiting, a page loaded directly would depend entirely on the
	// loader's own suppression having been honoured.
	useEffect(() => {
		if (isInIframe) return;
		return onUsercentricsReady((available) => {
			if (!available) return;
			void syncUsercentricsBannerVisibility(bannerFreeRoute).catch((error: unknown) => {
				console.error(new Error(`Failed to sync the Usercentrics banner visibility: ${String(error)}`));
			});
		});
	}, [isInIframe, bannerFreeRoute]);

	// With no banner in an embed, nothing would ever lift the head's denied Consent Mode default, so the
	// decision the integrator already collected has to be supplied here. See `grantEmbeddedConsent`.
	useEffect(() => {
		if (!isInIframe) return;
		grantEmbeddedConsent();
	}, [isInIframe]);

	// Act on the CMP's decision, once it is up and again on every change. Two things, on the same trigger
	// because they answer the same event: mirror the decision into a cookie so the NEXT server render
	// already knows it (see `syncConsentCookie`), and delete the analytics cookies when analytics is not
	// consented (see `clearCookiesForRefusedServices`) — Consent Mode stops gtag.js writing them but never
	// removes what an earlier acceptance already wrote, so withdrawal would otherwise leave the client id
	// in place for two years.
	useEffect(() => {
		if (isInIframe) return;
		const applyDecision = () => {
			syncConsentCookie();
			clearCookiesForRefusedServices();
		};
		const stopWaiting = onUsercentricsReady((available) => {
			if (available) applyDecision();
		});
		const unsubscribe = onUsercentricsConsentChanged(applyDecision);
		return () => {
			stopWaiting();
			unsubscribe();
		};
	}, [isInIframe]);

	// Keep the banner in the page's language. Switching locale is a client-side router push, never a
	// document reload, so the CMP would otherwise stay in the language it initialised with — see
	// `syncUsercentricsLanguage`. Depends on `locale` so it re-runs on every switch, not just on mount.
	useEffect(() => {
		void syncUsercentricsLanguage(locale).catch((error: unknown) => {
			console.error(new Error(`Failed to sync the Usercentrics banner language to "${locale}": ${String(error)}`));
		});
	}, [locale]);

	return (
		<html lang={locale}>
			<head>
				<HeadContent />

				{cdnUrl && <link rel="preconnect" href={cdnUrl} crossOrigin="anonymous" />}
				{imgproxyUrl && imgproxyUrl !== cdnUrl && <link rel="preconnect" href={imgproxyUrl} crossOrigin="anonymous" />}
				<link rel="stylesheet" href={appCssUrl} />
				<link
					rel="preload"
					href={`${cdnUrl}/rethink-sans-variable.woff2`}
					as="font"
					type="font/woff2"
					crossOrigin="anonymous"
				/>
				<style>
					{'@font-face {' +
						"font-family: 'Rethink Sans Variable';" +
						'font-style: normal;' +
						'font-display: swap;' +
						'font-weight: 300 800;' +
						`src: url('${cdnUrl}/rethink-sans-variable.woff2') format('woff2');` +
						'}'}
				</style>
			</head>

			<body className="min-h-dvh overflow-y-scroll">
				{children}
				<OnDemandToaster />

				<div className="hidden sm:block">
					<TanStackDevtools
						plugins={[
							{
								name: 'Tanstack Router',
								render: <TanStackRouterDevtoolsPanel />,
							},
							{
								name: 'Tanstack Query',
								render: <ReactQueryDevtoolsPanel />,
							},
							{
								name: 'Tanstack Form',
								render: <FormDevtoolsPanel />,
							},
						]}
					/>
				</div>
				<Scripts />
			</body>
		</html>
	);
}
